<template>
  <v-dialog v-model="open" max-width="640">
    <v-card>
      <v-card-title>
        <v-icon color="primary" class="me-2">mdi-account-child</v-icon>
        <span class="text-h6">{{ $t('students_of', { name: guardianName }) }}</span>
      </v-card-title>

      <v-card-subtitle class="pt-2">{{ $t('guardian_students_hint') }}</v-card-subtitle>

      <v-card-text>
        <vue-element-loading :active="isLoading" />

        <!-- who they look after now -->
        <div class="text-subtitle-2 mb-2">{{ $t('currently_linked') }}</div>
        <div v-if="linked.length" class="mb-4">
          <v-chip
            v-for="student in linked"
            :key="student.id"
            class="ma-1"
            close
            :close-icon="'mdi-close-circle'"
            color="primary"
            outlined
            @click:close="unlink(student)"
          >
            {{ student.name }}
            <span v-if="student.student_identification" class="ms-1 text-caption">
              ({{ student.student_identification }})
            </span>
          </v-chip>
        </div>
        <p v-else class="mb-4">{{ $t('no_students_linked') }}</p>

        <v-divider class="my-3"></v-divider>

        <!-- and who they could. Searched on the server, because a school with
             a few thousand students cannot put them all in a dropdown -->
        <div class="text-subtitle-2 mb-2">{{ $t('add_a_student') }}</div>
        <v-autocomplete
          v-model="toAdd"
          :items="candidates"
          :search-input.sync="search"
          :loading="isSearching"
          item-text="label"
          item-value="id"
          :label="$t('search_students')"
          :no-data-text="search ? $t('noResults') : $t('type_to_search')"
          :hint="$t('search_students_hint')"
          persistent-hint
          multiple
          chips
          deletable-chips
          clearable
          dense
          outlined
        ></v-autocomplete>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text :disabled="isSaving" @click="close">{{ $t('close') }}</v-btn>
        <v-btn
          color="primary"
          text
          :loading="isSaving"
          :disabled="toAdd.length === 0"
          @click="link"
        >
          {{ $t('add_selected') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/*
 * Which children a guardian looks after.
 *
 * Nothing in the panel could show this, let alone change it. A link was only
 * ever created as a side effect of adding a student, so a parent whose second
 * child joined, a grandparent who should see both grandchildren, or a couple
 * who separated all needed a spreadsheet import or a developer.
 *
 * Removing the last parent is refused by the server rather than allowed and
 * regretted: a student with no parent drops out of the students list entirely
 * and is eventually deleted by the nightly job, which should be a deliberate
 * act with its own confirmation, not a side effect of tidying up a chip.
 */
import VueElementLoading from "vue-element-loading";

export default {
  name: "GuardianStudentsDialog",
  components: { VueElementLoading },
  props: {
    value: { type: Boolean, default: false },
    guardian: { type: Object, default: null },
  },
  data() {
    return {
      linked: [],
      candidates: [],
      toAdd: [],
      search: "",
      searchTimer: null,
      isLoading: false,
      isSearching: false,
      isSaving: false,
    };
  },
  computed: {
    open: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    guardianName() {
      return this.guardian ? this.guardian.name : "";
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.toAdd = [];
        this.search = "";
        this.candidates = [];
        this.load();
      }
    },
    search(term) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      if (!term || term.length < 2) return;
      this.searchTimer = setTimeout(() => this.findStudents(term), 300);
    },
  },
  methods: {
    close() {
      this.open = false;
    },
    load() {
      if (!this.guardian) return;

      this.isLoading = true;
      axios
        .get("/school-students/guardian-students", {
          params: { guardian_id: this.guardian.id },
        })
        .then((response) => {
          this.linked = response.data.students || [];
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_retrieving_users"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    //the paged list endpoint doubles as the student search
    findStudents(term) {
      this.isSearching = true;
      axios
        .get("/users/list", {
          params: { type: "students", search: term, per_page: 20, page: 1 },
        })
        .then((response) => {
          const linkedIds = this.linked.map((student) => student.id);
          this.candidates = (response.data.data || [])
            .filter((student) => linkedIds.indexOf(student.id) === -1)
            .map((student) => ({
              id: student.id,
              label: student.student_identification
                ? `${student.name} (${student.student_identification})`
                : student.name,
            }));
        })
        .catch(() => {
          this.candidates = [];
        })
        .then(() => {
          this.isSearching = false;
        });
    },
    link() {
      this.isSaving = true;
      axios
        .post("/school-students/link-guardian", {
          guardian_id: this.guardian.id,
          student_ids: this.toAdd,
        })
        .then(() => {
          this.$notifySuccess(
            this.$t("guardian_linked", { count: this.toAdd.length, name: this.guardianName })
          );
          this.toAdd = [];
          this.search = "";
          this.candidates = [];
          this.load();
          this.$emit("changed");
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_linking_guardian"));
        })
        .then(() => {
          this.isSaving = false;
        });
    },
    unlink(student) {
      this.$confirmAction({
        title: this.$t("remove_student_from_guardian", { name: student.name }),
        text: this.$t("confirm_unlink_guardian", {
          student: student.name,
          guardian: this.guardianName,
        }),
        icon: "warning",
        confirmText: this.$t("yes"),
      }).then((confirmed) => {
        if (!confirmed) return;

        this.isSaving = true;
        axios
          .post("/school-students/unlink-guardian", {
            guardian_id: this.guardian.id,
            student_ids: [student.id],
          })
          .then((response) => {
            const refused = response.data.refused || [];
            if (refused.length) {
              //the server refuses to leave a child with nobody, and says why
              this.$notifyError(
                { response: { data: { message: refused[0].message } } },
                this.$t("error_unlinking_guardian")
              );
            } else {
              this.$notifySuccess(this.$t("guardian_unlinked", { name: student.name }));
            }
            this.load();
            this.$emit("changed");
          })
          .catch((error) => {
            this.$notifyError(error, this.$t("error_unlinking_guardian"));
          })
          .then(() => {
            this.isSaving = false;
          });
      });
    },
  },
};
</script>
